import React, { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { User, KeyRound, LogOut, Building2, Mail, Phone, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const roleLabel = { admin: 'Super Admin · Scintilla Hub', partner: 'B.O. (Back Office)', area_manager: 'Area Manager', agent: 'Agente Commerciale' };
const statusLabel = { active: 'Attivo', pending: 'In attesa', disabled: 'Disabilitato' };

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next1, setNext1] = useState('');
  const [next2, setNext2] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (next1 !== next2) { toast.error('Le nuove password non coincidono'); return; }
    if (next1.length < 6) { toast.error('La nuova password deve avere almeno 6 caratteri'); return; }
    setSaving(true);
    try {
      await api.changePassword(current, next1);
      toast.success('Password aggiornata');
      setCurrent(''); setNext1(''); setNext2('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const company = user.company;

  return (
    <div>
      <PageHeader title="Il mio profilo" description="Dati del tuo account, accesso e sicurezza" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="size-4" />Dati account</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><div className="text-xs text-muted-foreground">Nome completo</div><div className="font-medium mt-0.5">{user.full_name || '—'}</div></div>
                <div><div className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="size-3" />Email</div><div className="font-medium mt-0.5 font-mono text-xs">{user.email}</div></div>
                <div><div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="size-3" />Telefono</div><div className="font-medium mt-0.5">{user.phone || '—'}</div></div>
                <div><div className="text-xs text-muted-foreground flex items-center gap-1"><Shield className="size-3" />Ruolo</div><div className="mt-0.5"><Badge variant="secondary">{roleLabel[user.role]}</Badge></div></div>
                <div><div className="text-xs text-muted-foreground">Stato</div><div className="mt-0.5"><Badge>{statusLabel[user.status]}</Badge></div></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><KeyRound className="size-4" />Cambia password</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
                <div className="space-y-1.5"><Label htmlFor="cur">Password attuale</Label><Input id="cur" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" data-testid="profile-current-password" /></div>
                <div className="space-y-1.5"><Label htmlFor="new1">Nuova password</Label><Input id="new1" type="password" value={next1} onChange={(e) => setNext1(e.target.value)} required minLength={6} autoComplete="new-password" data-testid="profile-new-password" /></div>
                <div className="space-y-1.5"><Label htmlFor="new2">Conferma nuova password</Label><Input id="new2" type="password" value={next2} onChange={(e) => setNext2(e.target.value)} required minLength={6} autoComplete="new-password" data-testid="profile-new-password2" /></div>
                <div className="flex justify-end"><Button type="submit" disabled={saving} data-testid="profile-change-password-button">{saving ? 'Salvataggio...' : 'Aggiorna password'}</Button></div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          {company && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Building2 className="size-4" />Azienda</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  {company.logo_data_url ? <img src={company.logo_data_url} alt="" className="size-12 rounded border bg-white object-contain p-1" /> : <div className="size-12 rounded border bg-secondary grid place-items-center"><Building2 className="size-5 text-muted-foreground" /></div>}
                  <div><div className="font-medium">{company.name}</div><div className="text-xs text-muted-foreground">{[company.city, company.province].filter(Boolean).join(', ')}</div></div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Sessione</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">Termina la sessione corrente in tutti i dispositivi.</p>
              <Button variant="outline" className="w-full" onClick={() => { logout(); navigate('/login'); }} data-testid="profile-logout-button"><LogOut className="size-4 mr-2" />Esci</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
